import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import OrgMemberAvatar from "@/components/avatars/OrgMemberAvatar";
import MemberProfileHover from "@/components/MemberProfileHover";
import { Calendar, Pencil, Sparkles, MessageCircle, ExternalLink, Columns3, ChevronRight } from "lucide-react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getFullName } from "@/lib/userUtils";
import { cn } from "@/lib/utils";
import { useAuth } from "@/scripts/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { authUserIdFromToken } from "@/lib/tokenUtils";
import PostThreadView from "@/components/PostThreadView";
import MarkdownBody from "@/components/MarkdownBody";
import { CatchUpBriefing } from "@/components/CatchUpCard";
import ShareToTeamChannelDialog from "@/components/ShareToTeamChannelDialog";
import { API_BASE_URL } from "@/lib/apiBase";
import {
  entitlementMessageFor,
  getEntitlementCode,
  toastEntitlementOrError,
} from "@/lib/entitlementToast";
import { messageForCatchupGenerateError } from "@/lib/catchupGenerateError";
import {
  workKindOf,
  workKindLabel,
  parentIdOf,
  storyPointsOf,
  childCountsOf,
  postIdOfWork,
  parentChainOf,
  epicHorizonOf,
  cardDueAt,
  cardStartsAt,
  cardLaneOf,
  isCardOverdue,
  linkedWorkSet,
  sortLinkedWork,
} from "@/lib/workKind";
import { cardAuthorUser, isIntakeAuthor } from "@/lib/cardAttribution";

export const isCardFrozen = (card) =>
  Boolean(card?.handoff_frozen || card?.pending_handoff);

export function cardAssignees(card) {
  const list = card?.assignees || card?.Assignees;
  if (Array.isArray(list) && list.length) return list;
  const single = card?.assignee || card?.Assignee;
  return single ? [single] : [];
}

export function ParentBreadcrumb({
  card,
  hierarchyCards,
  onFocusParent,
  onOpenRemoteParent,
}) {
  const chain = parentChainOf(card, hierarchyCards);
  if (!chain.length) return null;
  const localIds = new Set(
    (hierarchyCards || []).map((c) => postIdOfWork(c)).filter(Boolean)
  );
  return (
    <span className="flex items-center gap-1 min-w-0 max-w-full flex-wrap">
      {chain.map((parent, i) => {
        const id = postIdOfWork(parent);
        const label = `${workKindLabel(parent)} ${parent.title || "Untitled"}`;
        const isLocal = id && localIds.has(id);
        return (
          <span key={id || i} className="flex items-center gap-1 min-w-0">
            {i > 0 ? (
              <span className="text-muted-foreground text-xs shrink-0" aria-hidden>
                ›
              </span>
            ) : null}
            <button
              type="button"
              className="text-xs font-normal text-muted-foreground hover:text-foreground hover:underline truncate max-w-[9rem] text-left"
              title={label}
              onClick={(e) => {
                e.stopPropagation();
                if (isLocal && onFocusParent) {
                  onFocusParent(parent);
                } else if (onOpenRemoteParent && id) {
                  onOpenRemoteParent(parent);
                } else if (onFocusParent) {
                  onFocusParent(parent);
                }
              }}
            >
              {label}
            </button>
          </span>
        );
      })}
    </span>
  );
}

function epicChildrenMissingCount(epic, pool) {
  const counts = childCountsOf(epic);
  if (!counts) return 0;
  const focusId = postIdOfWork(epic);
  const directFound = (pool || []).filter((c) => parentIdOf(c) === focusId).length;
  return Math.max(0, counts.total - directFound);
}

const HORIZON_LABELS = { now: "Now", upcoming: "Upcoming", done: "Done" };

function WorkMetaChips({
  card,
  hierarchyCards,
  completedWorkLane,
  showKind = true,
  onOpenRemoteParent,
}) {
  if (!card) return null;
  const lane = cardLaneOf(card);
  const due = cardDueAt(card);
  const start = cardStartsAt(card);
  const overdue = isCardOverdue(card, completedWorkLane);
  const points = storyPointsOf(card);
  const assignees = cardAssignees(card);
  const horizon =
    workKindOf(card) === "epic"
      ? epicHorizonOf(card, { completedWorkLane })
      : null;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {showKind ? (
        <Badge variant="secondary" className="text-[10px]">
          {workKindLabel(card)}
        </Badge>
      ) : null}
      {horizon ? (
        <Badge variant="outline" className="text-[10px]">
          {HORIZON_LABELS[horizon.horizon] || horizon.horizon}
        </Badge>
      ) : null}
      {lane ? (
        <span className="text-xs text-muted-foreground">{lane}</span>
      ) : null}
      {points != null ? (
        <span className="text-xs text-muted-foreground">{points} pts</span>
      ) : null}
      {overdue ? (
        <Badge
          variant="outline"
          className="text-[10px] border-destructive/40 text-destructive"
        >
          Overdue
        </Badge>
      ) : null}
      {assignees.length > 0 ? (
        <span
          className="inline-flex items-center gap-1 text-xs text-muted-foreground"
          title={assignees.map((a) => getFullName(a)).join(", ")}
        >
          <span className="inline-flex -space-x-1">
            {assignees.slice(0, 3).map((a) => (
              <OrgMemberAvatar
                key={a.user_id || a.userId}
                user={a}
                className="h-4 w-4 ring-1 ring-background"
                fallbackClassName="text-[8px]"
              />
            ))}
          </span>
          {assignees.length === 1
            ? getFullName(assignees[0])
            : assignees.length > 3
              ? `+${assignees.length - 3}`
              : `${assignees.length}`}
        </span>
      ) : null}
      {start || due ? (
        <span
          className={cn(
            "inline-flex items-center gap-1 text-xs",
            overdue ? "text-destructive" : "text-muted-foreground"
          )}
        >
          <Calendar className="h-3 w-3" />
          {start && due
            ? `${start.toLocaleDateString()} – ${due.toLocaleDateString()}`
            : (start || due).toLocaleDateString()}
        </span>
      ) : null}
      <ParentBreadcrumb
        card={card}
        hierarchyCards={hierarchyCards}
        onOpenRemoteParent={onOpenRemoteParent}
      />
    </div>
  );
}

function InspectField({ label, children }) {
  return (
    <div className="min-w-0 space-y-1.5">
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="min-w-0 text-sm text-foreground break-words">{children}</div>
    </div>
  );
}

function sprintLabelOf(card) {
  const name = card?.sprint_name ?? card?.sprintName;
  if (name && String(name).trim()) return String(name).trim();
  const id = card?.sprint_id ?? card?.sprintId;
  if (id != null && Number(id) > 0) return `Sprint ${id}`;
  return "Unscheduled";
}

function CardInspectFields({
  card,
  hierarchyCards,
  completedWorkLane,
  onOpenRemoteParent,
  teamMemberIds = null,
  orgId = null,
  compactDescription = false,
}) {
  const [descExpanded, setDescExpanded] = useState(false);
  if (!card) return null;
  const lane = cardLaneOf(card);
  const due = cardDueAt(card);
  const start = cardStartsAt(card);
  const overdue = isCardOverdue(card, completedWorkLane);
  const points = storyPointsOf(card);
  const assignees = cardAssignees(card);
  const frozen = isCardFrozen(card);
  const priority = card.priority ? String(card.priority) : null;
  const hasDescription = Boolean(
    card.content && String(card.content).trim()
  );
  const chain = parentChainOf(card, hierarchyCards);
  const creator = cardAuthorUser(card);
  const creatorLabel = isIntakeAuthor(card, teamMemberIds)
    ? "Requested by"
    : "Created by";
  const clampDescription = compactDescription && hasDescription && !descExpanded;

  return (
    <dl className="min-w-0 space-y-4">
      <InspectField label="Type">{workKindLabel(card)}</InspectField>
      <InspectField label="Status">
        <span className="inline-flex items-center gap-2 flex-wrap">
          {lane || "No lane"}
          {frozen ? (
            <Badge variant="outline" className="text-[10px]">
              Handoff pending
            </Badge>
          ) : null}
          {overdue ? (
            <Badge
              variant="outline"
              className="text-[10px] border-destructive/40 text-destructive"
            >
              Overdue
            </Badge>
          ) : null}
        </span>
      </InspectField>
      <InspectField label="Sprint">{sprintLabelOf(card)}</InspectField>
      {chain.length > 0 ? (
        <InspectField label="Parent">
          <ParentBreadcrumb
            card={card}
            hierarchyCards={hierarchyCards}
            onOpenRemoteParent={onOpenRemoteParent}
          />
        </InspectField>
      ) : null}
      <InspectField label={creatorLabel}>
        {creator ? (
          <MemberProfileHover user={creator} orgId={orgId}>
            <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
              <OrgMemberAvatar
                user={creator}
                className="h-6 w-6 shrink-0"
                fallbackClassName="text-[10px]"
              />
              <span className="min-w-0 truncate">{getFullName(creator)}</span>
            </span>
          </MemberProfileHover>
        ) : (
          <span className="text-muted-foreground">Unknown</span>
        )}
      </InspectField>
      <InspectField label="Assignees">
        {assignees.length === 0 ? (
          <span className="text-muted-foreground">Unassigned</span>
        ) : (
          <span className="inline-flex flex-wrap items-center gap-2">
            {assignees.map((a) => (
              <MemberProfileHover
                key={a.user_id || a.userId}
                user={a}
                orgId={orgId}
              >
                <span className="inline-flex min-w-0 items-center gap-1.5">
                  <OrgMemberAvatar
                    user={a}
                    className="h-6 w-6 shrink-0"
                    fallbackClassName="text-[10px]"
                  />
                  <span className="min-w-0 break-words">{getFullName(a)}</span>
                </span>
              </MemberProfileHover>
            ))}
          </span>
        )}
      </InspectField>
      {priority ? (
        <InspectField label="Priority">
          {priority.charAt(0).toUpperCase() + priority.slice(1)}
        </InspectField>
      ) : null}
      {points != null ? (
        <InspectField label="Points">{points}</InspectField>
      ) : null}
      {start || due ? (
        <InspectField label={start && due ? "Dates" : start ? "Start" : "Due"}>
          <span className={overdue ? "text-destructive" : undefined}>
            {start && due
              ? `${start.toLocaleDateString()} – ${due.toLocaleDateString()}`
              : (start || due).toLocaleDateString()}
          </span>
        </InspectField>
      ) : null}
      <InspectField label="Description">
        {hasDescription ? (
          <div className="min-w-0 space-y-2">
            <div
              className={cn(
                "text-sm leading-relaxed prose prose-sm dark:prose-invert max-w-none break-words overflow-x-auto",
                clampDescription && "line-clamp-4"
              )}
            >
              <MarkdownBody content={card.content} className="text-sm" />
            </div>
            {compactDescription ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-0 text-xs"
                onClick={() => setDescExpanded((v) => !v)}
              >
                {descExpanded ? "Show less" : "Show more"}
              </Button>
            ) : null}
          </div>
        ) : (
          <span className="text-muted-foreground">No description</span>
        )}
      </InspectField>
    </dl>
  );
}

export default function CardInspectOverlay({
  epic,
  hierarchyCards,
  completedWorkLane,
  orgId,
  teamId = null,
  teamMemberIds = null,
  onOpenChange,
  onCardClick,
  onCardEdit,
  onOpenOnBoard = null,
  onOpenRemoteParent,
  defaultChannel = null,
  onViewTeamChannel,
  focusId = null,
}) {
  const { accessToken } = useAuth();
  const location = useLocation();
  const returnPath = `${location.pathname}${location.search || ""}`;
  const currentUserId = authUserIdFromToken(accessToken);
  const pool = useMemo(() => hierarchyCards || [], [hierarchyCards]);
  const epicId = postIdOfWork(epic);
  const isEpicRoot = workKindOf(epic) === "epic";
  const [selectedId, setSelectedId] = useState(focusId || epicId);
  const [summarizing, setSummarizing] = useState(false);
  const [entityBriefing, setEntityBriefing] = useState(null);
  const [summarizeMeta, setSummarizeMeta] = useState(null);
  const [summarizeError, setSummarizeError] = useState(null);
  const [discussOpen, setDiscussOpen] = useState(false);
  const isMobile = useIsMobile();
  const [mobileTab, setMobileTab] = useState("details");
  /** Nested reply drill-in within the inspect thread (no permalink URL). */
  const [threadFocusCommentId, setThreadFocusCommentId] = useState(null);

  const selectWorkItem = (id) => {
    if (
      isMobile &&
      isEpicRoot &&
      id != null &&
      selectedId != null &&
      Number(id) === Number(selectedId)
    ) {
      setMobileTab("details");
      return;
    }
    setSelectedId(id);
  };

  const openWorkItem = (id) => {
    setSelectedId(id);
    if (isMobile && isEpicRoot) {
      setMobileTab("details");
    }
  };

  useEffect(() => {
    setSelectedId(focusId || epicId);
  }, [epicId, focusId]);

  const linked = useMemo(() => {
    if (!epic) return [];
    return sortLinkedWork(linkedWorkSet(epic, pool)).filter(
      (c) => postIdOfWork(c) !== epicId
    );
  }, [epic, pool, epicId]);

  const stories = linked.filter((c) => workKindOf(c) === "story");
  const storyIds = new Set(stories.map((s) => postIdOfWork(s)));
  const tasksUnderStory = linked.filter(
    (c) => workKindOf(c) === "task" && storyIds.has(parentIdOf(c))
  );
  const otherChildren = linked.filter((c) => {
    const id = postIdOfWork(c);
    if (storyIds.has(id)) return false;
    if (workKindOf(c) === "task" && storyIds.has(parentIdOf(c))) return false;
    return true;
  });
  const counts = childCountsOf(epic);
  const missing = epicChildrenMissingCount(epic, pool);
  const selectedCard =
    (selectedId &&
      (selectedId === epicId
        ? epic
        : linked.find((c) => postIdOfWork(c) === selectedId) ||
          pool.find((c) => postIdOfWork(c) === selectedId))) ||
    epic;
  const selectedPostId = postIdOfWork(selectedCard);

  useEffect(() => {
    setMobileTab(isEpicRoot ? "work" : "details");
  }, [epicId, isEpicRoot]);

  useEffect(() => {
    setEntityBriefing(null);
    setSummarizeMeta(null);
    setSummarizeError(null);
    setThreadFocusCommentId(null);
  }, [selectedPostId]);

  const runEntitySummarize = useCallback(
    async ({ force } = {}) => {
      if (!accessToken || !orgId || !selectedPostId || summarizing) return;
      setSummarizing(true);
      setSummarizeError(null);
      try {
        const { data } = await axios.post(
          `${API_BASE_URL}/catchup/generate`,
          {
            scope: "entity",
            post_id: Number(selectedPostId),
            force: force === true,
          },
          {
            params: { org_id: orgId },
            headers: { Authorization: `Bearer ${accessToken}` },
            withCredentials: true,
          }
        );
        setEntityBriefing(data?.summary?.summary_json || null);
        setSummarizeMeta({
          fromCache: Boolean(data?.from_cache),
          reason: data?.reason || null,
          generatedAt: data?.summary?.generated_at || null,
        });
      } catch (err) {
        if (getEntitlementCode(err)) {
          const msg = entitlementMessageFor(err, { canManageSettings: false });
          setSummarizeError(msg);
          toastEntitlementOrError(err, "Failed to summarize card.", {
            canManageSettings: false,
            orgId,
          });
        } else {
          setSummarizeError(
            messageForCatchupGenerateError(err, {
              fallback: "Failed to summarize card.",
            })
          );
        }
      } finally {
        setSummarizing(false);
      }
    },
    [accessToken, orgId, selectedPostId, summarizing]
  );

  const cardById = (id) => {
    if (!id) return null;
    if (id === epicId) return epic;
    return linked.find((c) => postIdOfWork(c) === id) || null;
  };

  const NavRow = ({
    id,
    title,
    kindMark,
    lane,
    overdue,
    weight = "task",
    subtitle,
  }) => {
    const selected = Number(id) === Number(selectedId);
    const rowCard = cardById(id);
    const frozen = isCardFrozen(rowCard);
    return (
      <div
        className={cn(
          "group flex items-start gap-0.5 border-l-[3px] transition-colors",
          selected
            ? "border-l-primary bg-primary/10"
            : "border-l-transparent hover:bg-muted/50"
        )}
      >
        <button
          type="button"
          aria-label={title || "Untitled"}
          aria-current={selected ? "true" : undefined}
          className="min-w-0 flex-1 text-left px-3 py-2.5"
          onClick={() => selectWorkItem(id)}
        >
          <div className="flex items-baseline gap-2 min-w-0">
            {kindMark ? (
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground shrink-0">
                {kindMark}
              </span>
            ) : null}
            <span
              className={cn(
                "truncate text-foreground",
                weight === "story" ? "text-sm font-medium" : "text-sm font-normal",
                selected && "font-semibold"
              )}
            >
              {title || "Untitled"}
            </span>
            {selected && isMobile ? (
              <span className="text-[10px] uppercase tracking-wide text-primary shrink-0">
                Selected
              </span>
            ) : null}
          </div>
          {subtitle ? (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              {subtitle}
            </p>
          ) : null}
          {(lane || overdue) && !subtitle ? (
            <p
              className={cn(
                "text-xs mt-0.5 truncate",
                overdue ? "text-destructive" : "text-muted-foreground"
              )}
            >
              {[lane, overdue ? "Overdue" : null].filter(Boolean).join(" · ")}
            </p>
          ) : null}
        </button>
        {onCardEdit && rowCard && !frozen ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn(
              "h-8 w-8 shrink-0 mt-1 group-focus-within:opacity-100",
              isMobile
                ? "opacity-100"
                : "opacity-0 group-hover:opacity-100"
            )}
            aria-label={`Edit ${title || "card"}`}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedId(id);
              onCardEdit(rowCard);
            }}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        ) : null}
        {isMobile && isEpicRoot ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 mt-1 mr-1.5"
            aria-label={`Open ${title || "card"}`}
            onClick={(e) => {
              e.stopPropagation();
              openWorkItem(id);
            }}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
    );
  };

  const cardDetails = selectedCard ? (
    <div className="min-w-0 space-y-5">
      <div className="min-w-0 space-y-3">
        <h3 className="text-xl font-semibold text-foreground leading-snug break-words">
          {selectedCard.title || "Untitled"}
        </h3>
        <div
          className={cn(
            "flex flex-wrap items-center gap-1.5",
            isMobile && "flex-col items-stretch [&>button]:w-full"
          )}
        >
          {accessToken && selectedPostId ? (
            !entityBriefing ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={summarizing}
                onClick={() => runEntitySummarize({ force: false })}
              >
                <Sparkles className="h-4 w-4 mr-1.5" />
                {summarizing ? "Summarizing…" : "Summarize"}
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={summarizing}
                onClick={() => runEntitySummarize({ force: true })}
              >
                <Sparkles className="h-4 w-4 mr-1.5" />
                {summarizing ? "Updating…" : "Update summary"}
              </Button>
            )
          ) : null}
          {onCardEdit && !isCardFrozen(selectedCard) ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onCardEdit(selectedCard)}
            >
              <Pencil className="h-4 w-4 mr-1.5" />
              Edit
            </Button>
          ) : null}
          {onOpenOnBoard ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenOnBoard(selectedCard)}
            >
              <Columns3 className="h-4 w-4 mr-1.5" />
              Open on board
            </Button>
          ) : null}
          {onCardClick ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onCardClick(selectedCard)}
            >
              <ExternalLink className="h-4 w-4 mr-1.5" />
              Open in page
            </Button>
          ) : null}
          {selectedPostId && teamId && defaultChannel ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setDiscussOpen(true)}
            >
              <MessageCircle className="h-4 w-4 mr-1.5" />
              Discuss
            </Button>
          ) : null}
        </div>
      </div>
      <CardInspectFields
        card={selectedCard}
        hierarchyCards={pool}
        completedWorkLane={completedWorkLane}
        onOpenRemoteParent={onOpenRemoteParent}
        teamMemberIds={teamMemberIds}
        orgId={orgId}
        compactDescription={isMobile}
      />
      {(entityBriefing || summarizeError) ? (
        <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 space-y-2 min-w-0">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h4 className="text-sm font-semibold text-foreground">
              Card summary
            </h4>
            {summarizeMeta && !summarizeError ? (
              <p className="text-xs text-muted-foreground">
                {summarizeMeta.fromCache ||
                summarizeMeta.reason === "unchanged"
                  ? "Cached"
                  : "Just generated"}
                {summarizeMeta.generatedAt
                  ? ` · ${new Date(
                      summarizeMeta.generatedAt
                    ).toLocaleString()}`
                  : ""}
              </p>
            ) : null}
          </div>
          {summarizeError ? (
            <p className="text-sm text-destructive break-words" role="alert">
              {summarizeError}
            </p>
          ) : (
            <CatchUpBriefing
              briefing={entityBriefing}
              returnPath={returnPath}
              orgId={orgId}
              showThreadLinks={false}
              showPlaceLabel={false}
            />
          )}
        </div>
      ) : null}
    </div>
  ) : null;

  const threadContent =
    accessToken && selectedCard ? (
      <div className="flex min-h-0 flex-1 flex-col">
        <PostThreadView
          key={postIdOfWork(selectedCard)}
          variant="page"
          hidePageHeader={isMobile || !isEpicRoot}
          post={selectedCard}
          isOpen
          accessToken={accessToken}
          currentUserId={currentUserId}
          orgId={orgId}
          teamMemberIds={teamMemberIds}
          onClose={() => onOpenChange?.(false)}
          focusCommentId={threadFocusCommentId}
          onContinueThread={setThreadFocusCommentId}
          onExitFocus={() => setThreadFocusCommentId(null)}
        />
      </div>
    ) : null;

  const mobileTabs = isEpicRoot
    ? [
        { id: "work", label: "Work" },
        { id: "details", label: "Details" },
        { id: "thread", label: "Thread" },
      ]
    : [
        { id: "details", label: "Details" },
        { id: "thread", label: "Thread" },
      ];

  return (
    <>
      <Dialog open={Boolean(epic)} onOpenChange={onOpenChange}>
      <DialogContent className="left-0 top-0 flex h-[100dvh] w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden overflow-x-hidden rounded-none border-0 p-0 sm:rounded-none">
        {isEpicRoot ? (
        <DialogHeader className="shrink-0 space-y-2 border-b border-border px-4 py-3 pr-12 text-left sm:px-6">
          <DialogTitle className="text-lg">
            {epic?.title || "Epic"}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2">
              {counts ? (
                <p className="text-sm text-muted-foreground">
                  {counts.done} of {counts.total} done
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Stories and tasks linked to this epic
                </p>
              )}
              <WorkMetaChips
                card={epic}
                hierarchyCards={pool}
                completedWorkLane={completedWorkLane}
                showKind
                onOpenRemoteParent={onOpenRemoteParent}
              />
            </div>
          </DialogDescription>
        </DialogHeader>
        ) : (
        <DialogHeader className="sr-only">
          <DialogTitle>{epic?.title || "Card"}</DialogTitle>
          <DialogDescription>
            Card details and thread
          </DialogDescription>
        </DialogHeader>
        )}
        {isMobile ? (
          <div
            className="shrink-0 flex border-b border-border md:hidden"
            role="tablist"
            aria-label="Card sections"
          >
            {mobileTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={mobileTab === tab.id}
                className={cn(
                  "flex-1 py-3 text-sm font-medium border-b-2 transition-colors",
                  mobileTab === tab.id
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground"
                )}
                onClick={() => setMobileTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        ) : null}
        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          {isEpicRoot && (!isMobile || mobileTab === "work") ? (
          <aside
            className={cn(
              "min-h-0 w-full shrink-0 overflow-y-auto border-b border-border md:h-full md:w-[38%] md:border-b-0 md:border-r",
              isMobile && "flex flex-1 flex-col"
            )}
          >
            {linked.length === 0 ? (
              <p className="text-sm text-muted-foreground px-3 py-4">
                No stories or tasks linked to this epic yet.
              </p>
            ) : (
              <nav className="divide-y divide-border" aria-label="Epic work">
                <NavRow
                  id={epicId}
                  title="Overview"
                  subtitle={epic?.title || "Epic"}
                  weight="story"
                />
                {stories.map((story) => {
                  const sid = postIdOfWork(story);
                  const nested = tasksUnderStory.filter(
                    (t) => parentIdOf(t) === sid
                  );
                  return (
                    <div key={sid}>
                      <p className="px-3 pt-3 pb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        Story
                      </p>
                      <NavRow
                        id={sid}
                        title={story.title}
                        weight="story"
                        lane={cardLaneOf(story)}
                        overdue={isCardOverdue(story, completedWorkLane)}
                      />
                      {nested.map((task) => (
                        <NavRow
                          key={postIdOfWork(task)}
                          id={postIdOfWork(task)}
                          title={task.title}
                          kindMark="Task"
                          weight="task"
                          lane={cardLaneOf(task)}
                          overdue={isCardOverdue(task, completedWorkLane)}
                        />
                      ))}
                    </div>
                  );
                })}
                {otherChildren.length > 0 ? (
                  <div>
                    <p className="px-3 pt-3 pb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      Also linked
                    </p>
                    {otherChildren.map((child) => (
                      <NavRow
                        key={postIdOfWork(child)}
                        id={postIdOfWork(child)}
                        title={child.title}
                        kindMark={workKindLabel(child)}
                        weight={workKindOf(child) === "story" ? "story" : "task"}
                        lane={cardLaneOf(child)}
                        overdue={isCardOverdue(child, completedWorkLane)}
                      />
                    ))}
                  </div>
                ) : null}
              </nav>
            )}
            {missing > 0 ? (
              <p className="text-xs text-muted-foreground px-3 py-3">
                {missing} linked card{missing === 1 ? "" : "s"} are not loaded
                on this board.
              </p>
            ) : null}
          </aside>
          ) : null}
          {!isEpicRoot && (!isMobile || mobileTab === "details") ? (
          <aside
            className={cn(
              "min-h-0 min-w-0 w-full shrink-0 overflow-y-auto overflow-x-hidden border-b border-border p-4 sm:p-6 md:h-full md:w-1/2 lg:w-[46%] md:border-b-0 md:border-r",
              isMobile ? "flex flex-1 flex-col pr-4" : "pr-12"
            )}
          >
            {cardDetails}
          </aside>
          ) : null}
          {isEpicRoot && isMobile && mobileTab === "details" ? (
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden p-4">
              {cardDetails}
            </div>
          ) : null}
          {!isMobile || mobileTab === "thread" ? (
          <section
            className={cn(
              "min-h-0 min-w-0 flex-1 flex flex-col overflow-x-hidden",
              isMobile ? "overflow-hidden p-3" : "overflow-y-auto p-4 sm:p-6",
              !isEpicRoot && !isMobile && "pr-12"
            )}
          >
            {isEpicRoot ? (
              selectedCard ? (
                <div className="flex min-h-0 min-w-0 flex-1 flex-col space-y-4 max-w-3xl w-full">
                  {!isMobile ? (
                    <div className="shrink-0 overflow-y-auto max-h-[40%]">{cardDetails}</div>
                  ) : null}
                  <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                    {threadContent}
                  </div>
                </div>
              ) : null
            ) : (
              threadContent
            )}
          </section>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
    <ShareToTeamChannelDialog
      open={discussOpen}
      onOpenChange={setDiscussOpen}
      orgId={orgId}
      teamId={teamId}
      post={selectedCard}
      channel={defaultChannel}
      onViewChannel={onViewTeamChannel}
    />
    </>
  );
}

